import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ERR_TYPE_ENUM_IS_DROPDOWN,
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_DROPDOWN,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXT,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';
import useGetDefaultDistanceUnit from '/business/shared/hooks/useGetDefaultDistanceUnit';
import getVehicleTypesSelector from '/business/shared/state/selectors/application/step1/getVehicleTypesSelector';
import vehicleTypeDisplayName from '/business/shared/util/vehicleTypeDisplayName';
import type { QuestionInputField, QuestionSchemaType, VehicleType } from '/index';
import useGetEmissionTypeLabel from '/business/shared/hooks/useGetEmissionTypeLabel';

type vehValueType = {
  name: string | null;
  displayNameKey: string;
};

export default function useDownstreamLeasedVehiclesEstimatedSchema(): QuestionSchemaType {
  const { t } = useTranslation();
  const vehicleTypes: VehicleType | undefined = useSelector(getVehicleTypesSelector);

  const [vehicleValue, setVehicleValue] = useState<string | null>(null);
  const { distanceUnit } = useGetDefaultDistanceUnit();
  const { formatFuelId } = useGetEmissionTypeLabel();

  const vehTypesValues: vehValueType[] = useMemo(
    () => [
      {
        name: null,
        displayNameKey: 'Select vehicle type',
      },
      ...(vehicleTypes?.items.map(({ name, title }) => {
        return {
          name,
          displayNameKey: vehicleTypeDisplayName(name, title),
        };
      }) ?? []),
    ],
    [vehicleTypes?.items],
  );

  const lesseeId: QuestionInputField = useMemo(() => {
    return {
      propName: 'lesseeId',
      element: 'lessee_id',
      type: FIELD_TYPE_TXT,
      label: 'Lessee ID',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOLESSEEID',
          errStr: 'Please enter a valid identifier',
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRLESSEEIDRTOOLONG',
          errStr: 'Please enter a valid identifier',
          value: 3,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRLESSEEIDTOOSHORT',
          errStr: 'Please enter a valid identifier',
          value: 3000,
        },
      ],
    };
  }, []);

  const vehicleType: QuestionInputField = useMemo(() => {
    return {
      propName: 'vehicleType',
      type: FIELD_TYPE_DROPDOWN,
      element: 'vehicle_type',
      label: 'Vehicle type',
      values: vehTypesValues,
      onChangeCallback: (value: string) => {
        return setVehicleValue(value);
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOVEHICLETYPE',
          errStr: 'Please select a vehicle type',
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRVEHICLETYPENOTFOUND',
          errStr: 'Please select a vehicle type',
        },
      ],
    };
  }, [vehTypesValues]);

  const fuelType: QuestionInputField = useMemo(() => {
    return {
      propName: 'fuelType',
      type: FIELD_TYPE_DROPDOWN,
      element: 'fuel_type',
      label: 'Emission source:',
      saveMe: vehicleValue !== null,
      // renderMe: vehicleValue,
      validateMe: vehicleValue !== null,
      isDisabled: !vehicleValue,
      values: vehicleValue
        ? [
            {
              name: null,
              displayNameKey: 'Select fuel type',
            },
            // Save the title (the system_id), as getFuelTypesSelector does - `name` is a display slug.
            ...(vehicleTypes?.items
              .find((vehType) => vehType.name === vehicleValue)
              ?.children?.items.map(({ title }) => {
                return { name: title, displayNameKey: formatFuelId(title) };
              }) ?? []),
          ]
        : [{ name: null, displayNameKey: 'Select vehicle type first' }],
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNOVEHICLETYPE',
          errStr: 'Please select a fuel type',
        },
        {
          rule: ERR_TYPE_ENUM_IS_DROPDOWN,
          errCode: 'ERRVEHICLETYPENOTFOUND',
          errStr: 'Please select a fuel type',
        },
      ],
    };
  }, [vehicleTypes, vehicleValue]);

  const distanceTravelled: QuestionInputField = useMemo(() => {
    return {
      propName: 'distance',
      element: 'distance',
      type: FIELD_TYPE_NUMBER,
      label: 'Maximum yearly driving distance',
      placeholderI18nKey: 'Enter distance',
      inputMode: 'decimal',
      step: '.01',
      dropdown: {
        propName: 'distanceUnit',
        element: 'distance_unit',
        defaultValue: distanceUnit,
        values: [
          {
            name: 'km',
            displayNameKey: t('business:emissionFactors.distance.km', 'km'),
          },
          {
            name: 'miles',
            displayNameKey: t('business:emissionFactors.distance.miles', 'miles'),
          },
        ],
      },
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNODISTANCE',
          errStr: 'Please enter a valid distance',
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          value: 999999,
          errCode: 'ERRDISTANCETOOHIGH',
          errStr: 'Please enter a valid distance',
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          value: 0.1,
          errCode: 'ERRDISTANCETOOLOW',
          errStr: 'Please enter a valid distance',
        },
      ],
    };
  }, [t]);

  const dataSource: QuestionInputField = useMemo(() => {
    return {
      type: FIELD_TYPE_TXTAREA,
      propName: 'dataSource',
      element: 'data_source',
      labelI18nKey: 'business:QuestionPage.forms.deicing.para5',
      labelLayout: 'heading',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
          value: 5,
        },
        {
          rule: ERR_TYPE_STR_LONG,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
          value: 3000,
        },
      ],
    };
  }, [t]);

  const pageSchema: QuestionInputField[] = useMemo(() => {
    return [lesseeId, vehicleType, fuelType, distanceTravelled, dataSource];
  }, [lesseeId, vehicleType, fuelType, distanceTravelled, dataSource]);
  return {
    pageSchema,
  };
}
